<template>
    <b-container id="question-main-layout" fluid class=" px-0">

        <!-- Question & Question info 영역 -->
        <div class="px-5 py-4 mb-2">
            <div class="f-85 gray-550 float-right text-right" style="max-width: 400px;">
                {{ campaign_name }}
            </div>
            <h5 v-show="!isFieldEmpty(question_name)"
                :class="[{'mb-3': !isFieldEmpty(question_infobox)}, 'fw-600']"
            >
                {{ question_name }}
            </h5>
            <div v-show="!isFieldEmpty(question_infobox)"
                 id="question_infobox"
                 :class="[{ 'f-90 p-2 px-3 gray-600 bgray-200 rounded': !isFieldEmpty(question_infobox) }]"
                 v-html="question_infobox"
            >
            </div>
        </div>

        <!-- Sub-component 영역 -->
        <div v-if="!subs_loaded" class="text-center">
            <b-spinner class="mt-5 grey"></b-spinner>
            <div v-if="show_download_alt_pdf">
                <b-alert variant="warning" show class="mx-4 mt-4">
                    <div class="d-flex flex-column align-items-center justify-content-center">
                        <div class="d-flex align-items-center justify-content-center">
                            <b-icon-exclamation-triangle-fill font-scale="1.5"
                                                              class="mr-3"></b-icon-exclamation-triangle-fill>

                            <div class="flex-grow-1">
                                <div class="text-left">
                                    {{ $t('문항 로딩 시간이 20초를 초과했습니다.') }}

                                </div>
                                <div class="text-left">
                                    {{ $t('문제가 지속해서 발생한다면 PDF를 다운로드하여 질문에 답변을 하신 뒤,') }}
                                </div>
                                <div class="text-left">
                                    {{ $t('아래 메일로 답변지를 보내주십시오.') }}
                                </div>
                            </div>
                        </div>
                        <b-input-group class="mt-3 width-calc50">
                            <b-input-group-prepend>
                                <b-button href="mailto:info@qesg.co.kr" variant="outline-secondary"
                                          type="button"
                                >
                                    <b-icon-envelope-fill></b-icon-envelope-fill>
                                </b-button>
                            </b-input-group-prepend>

                            <b-form-input
                                value="info@qesg.co.kr"
                                readonly
                                class="text-left font-weight-bold"
                            ></b-form-input>

                            <b-input-group-append>
                                <b-button variant="outline-secondary" @click="copyEmail">{{ $t('복사') }}</b-button>
                            </b-input-group-append>
                        </b-input-group>
                        <div class="mt-3">
                            <b-button type="download" :download="`${question_name}.pdf`"
                                      :href="`/questions/${this.$route.params.question_code}_${this.$i18n.locale}.pdf`">
                                <b-icon-download class="mr-2"></b-icon-download>
                                {{ $t('PDF 다운로드') }}
                            </b-button>
                        </div>
                    </div>
                </b-alert>
            </div>
        </div>
        <div v-else-if="subs_loaded && subs.length > 0" class="px-5">
            <!-- 캠페인별 위젯 (custom hyundai-v2/hmc-theme Questions 이관) -->
            <div v-if="show_management_level" class="mb-3">
                <ManagementLevelTable :code-id="currentCodeId"
                                      :company-id="company_id || $route.query.cpn"
                                      :campaign-id="widget_campaign_id"
                                      :project-code="$route.query.pc || custom_project_code"
                                      :sub-codes="currentSubCodes"
                                      :subs-data="subs"
                />
            </div>
            <div v-if="themeScoredSubCodes.length > 0" class="mb-3">
                <ThemeLevelSummary :company-id="company_id || $route.query.cpn"
                                   :campaign-id="widget_campaign_id"
                                   :project-code="$route.query.pc || custom_project_code"
                                   :sub-codes="themeScoredSubCodes"
                                   :subs-data="subs"
                />
            </div>
            <sub-component v-for="item in subs"
                           :key="item.sub_code + pipeline_key"
                           :ref="'sub_components'"
                           :sub_type="item.sub_type"
                           :item="item"
                           :last_save_state="map_save_state_msg[item.sub_code]"
                           :is_disabled_by_parent="is_disabled_by_parent"
                           @update-pipeline="(msg) => handleUpdatePipeline(item.sub_code, msg)"
            >
            </sub-component>
        </div>
        <div v-else-if="subs.length === 0" class="px-5">
            <b-card class="bgray-200"> {{ $t('다음 질문으로 이동하여 주십시오.') }}</b-card>
        </div>

        <!-- 완료 체크 및 문항 이동 영역 -->
        <div class="px-5 py-5 mb-5">
            <template v-if="subs.length > 0 && is_completed !== 'true' && is_disabled_by_parent !== true">
                <div class="f-90 mr-2 red indent-lg text-right">
                    <fa icon="info-circle" class="mr-1"></fa>
                    <span v-if="$i18n.locale === 'ko'">
                        답변을 완료하셨다면 <b>'{{ question_name }} 완료하기'</b> 체크 후 다음으로 이동해주세요.
                    </span>
                    <span v-else-if="$i18n.locale === 'en'">
                        If you have completed the answer, check the 'Complete' and move to the ‘Next’.
                    </span>
                    <span v-else-if="$i18n.locale === 'ca'">
                        如果您已完成答案，请选中 <b>完成 {{ question_name }}</b> 并移至下一个。
                    </span>
                </div>
            </template>

            <div class="d-flex align-items-center justify-content-end mt-2">
                <template v-if="subs.length > 0 && is_completing">
                    <div class="border border-color-blue rounded p-2">
                        <b-spinner class="blue"></b-spinner>
                    </div>
                </template>
                <template v-else-if="subs.length > 0 && !is_completing">
                    <div v-b-tooltip.hover
                         :class="[is_completed === 'true' ? 'border border-color-blue' : 'border border-color-red', 'cursor rounded p-2']"
                         :title="is_disabled_by_parent ? `${$t('이미 제출되어 완료를 해제할 수 없습니다.')}` : `${$t('최종 제출 전까지 완료를 해제하고 답변을 수정할 수 있습니다.')}`"
                    >
                        <template v-if="mode !== 'public'">
                            <b-form-checkbox v-model="is_completed"
                                             class="d-inline blue-check"
                                             value="true"
                                             unchecked-value="false"
                                             :disabled="is_disabled_by_parent"
                                             @change="toggleProgress"
                            >
                                <span :class="is_completed === 'true ' ? 'blue' : 'red'">
                                    {{
                                        is_completed == 'true' ? $t('완료됨') : $t("완료하기", {question_name: question_name})
                                    }}
                                </span>
                            </b-form-checkbox>
                        </template>
                    </div>
                </template>

                <b-button class="ml-2 py-2"
                          variant="dark-border"
                          :disabled="is_completing"
                          @click.stop.prevent="goForward"
                >
                    {{ $t('다음') }}
                    <fa icon="caret-right" class="ml-2"></fa>
                </b-button>
            </div>
        </div>
    </b-container>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import SubComponent from '@core_components/client/components/subs/SubComponent';
import dayjs from 'dayjs';
import ManagementLevelTable from '@shared_components/common/ManagementLevelTable.vue';
import ThemeLevelSummary from '@shared_components/common/ThemeLevelSummary.vue';
import themeSubtopics from '@shared/mixin/report/raw-data/theme-subtopics.json';
import mltCampaignConfig from '@shared/mixin/report/raw-data/code_level_config_hyundai_v2.json';

export default {
    name: 'Questions',
    props: {
        question_code: {
            type: String,
            default: ''
        }
    },
    components: {
        SubComponent,
        ManagementLevelTable,
        ThemeLevelSummary
    },
    data() {
        return {
            template_name: '',
            is_completing: false,
            pipeline_key: 100,
            map_save_state_msg: {},
            loading_time: null,
            loading_time_interval: null,
            show_download_alt_pdf: false,
        };
    },
    computed: {
        ...mapState('questions', ['subs', 'subs_loaded',]),
        ...mapState(['company_id', 'campaign_in_progress', 'question_in_progress', 'question_in_progress_loaded', 'user_id', 'template_in_progress', 'mode']),
        ...mapState('industry_campaigns', ['custom_project_code', 'custom_template', 'custom_template_loaded']),
        // ── 캠페인별 위젯 게이팅 (custom hyundai-v2/hmc-theme 앱 이관) ──
        // custom 과 동일하게 데이터 기반 자기게이팅만 쓴다 (MLT=캠페인 config 키, TLS=테마 sub_code 화이트리스트).
        // 캠페인 상수/렌더러 키 게이트를 덧씌우면 테스트 캠페인(예: 559)처럼 상수 밖 캠페인에서 위젯이 사라진다 (2026-09-03).
        widget_campaign_id() {
            return this.$route.query.cmp || this.campaign_in_progress?.campaign_id;
        },
        currentCodeId() {
            const name = this.question_in_progress?.question_name || '';
            const match = name.match(/^([A-Z]-\d+-\d+)/);
            return match ? match[1] : '';
        },
        currentSubCodes() {
            if (!this.subs || this.subs.length === 0) return [];
            return this.subs.map(sub => sub.sub_code).filter(Boolean);
        },
        // MLT 는 내부 empty 상태가 어정쩡해서 config 존재를 페이지 레벨에서 게이트 (조사 권고)
        show_management_level() {
            if (this.mode === 'public') return false;
            return !!this.currentCodeId
                && !!mltCampaignConfig[String(this.widget_campaign_id || '')];
        },
        themeScoredSubCodes() {
            if (this.mode === 'public') return [];
            if (!this.subs || this.subs.length === 0) return [];
            return this.subs.map(s => s.sub_code).filter(code => code && themeSubtopics.byCode && themeSubtopics.byCode[code]);
        },
        campaign_name() {
            if (this.$i18n.locale === 'en') return this.campaign_in_progress.campaign_name_e;
            else if (this.$i18n.locale === 'ca') return this.campaign_in_progress.campaign_name_c;
            else return this.campaign_in_progress.campaign_name;
        },
        question_name() {
            if (this.$i18n.locale === 'en') return this.question_in_progress.question_name_e;
            else if (this.$i18n.locale === 'ca') return this.question_in_progress.question_name_c;
            else return this.question_in_progress.question_name;
        },
        question_infobox() {
            if (this.$i18n.locale === 'en') return this.question_in_progress.question_infobox_e;
            else if (this.$i18n.locale === 'ca') return this.question_in_progress.question_infobox_c;
            else return this.question_in_progress.question_infobox;
        },
        question_contents() {
            return this.question_in_progress.question_contents;
        },
        question_comment() {
            return this.question_in_progress.comment;
        },
        is_completed: {
            get() {
                return this.question_in_progress.is_completed;
            },
            set(newVal) {
                let temp_obj = {...this.question_in_progress};
                temp_obj.is_completed = newVal;
                this.$store.commit('update_question_in_progress', temp_obj);
            }
        },
        is_disabled_by_parent() {
            if (this.campaign_in_progress.campaign_state === '답변중' && this.campaign_in_progress.status_code === 'in_progress') return false;
            else if (this.campaign_in_progress.campaign_state === '생성/편집' && this.campaign_in_progress.status_code == 'in_progress') return false;
            else return true;
        },
        another_question_list() {
            let new_list = [];
            this.template_in_progress.template_contents.forEach(x => {
                x.children.forEach(y => {
                    y.children.forEach(z => new_list.push(z));
                });
            });
            return new_list;
        }
    },
    watch: {
        'subs_loaded': {
            immediate: true,
            handler(value) {
                if (value) {
                    this.resetLoadingCheckProcess();
                    return;
                }
                this.map_save_state_msg = {};
                if(this.loading_time_interval != null){
                    this.resetLoadingCheckProcess();
                }

                this.loading_time = dayjs();
                this.loading_time_interval = setInterval(() => {
                    let now = dayjs();
                    let diff = now.diff(this.loading_time, 'second');
                    if (diff > 20 && !this.subs_loaded) {
                        this.show_download_alt_pdf = true;
                    }
                }, 1000); // 10초마다 체크

               
            }
        }
    },
    created() {
    },
    beforeDestroy() {
        this.$emit('toggle-writable', true);
        this.clearPrevAttachments();

        this.resetLoadingCheckProcess();
    },
    methods: {
        ...mapActions('attachments', ['clearPrevAttachments']),
        resetLoadingCheckProcess(){
            clearInterval(this.loading_time_interval);
            this.loading_time_interval = null;
            this.loading_time = null;
            this.show_download_alt_pdf = false;
        },
        async toggleProgress() {
            await this.saveQuestionInProgress();
            await this.saveProgressLog();
        },
        async saveQuestionInProgress() {
            this.is_completing = true;
            await this.$store.dispatch('saveQuestionInProgress', this.question_in_progress);
            this.$emit('refresh-tree'); //for tree update
            this.is_completing = false;
        },
        async saveProgressLog() {
            try {
                let progress_rate = this.$store.getters.getProgressRate;
                // const path = this.$store.state.backend_host + '/save_progress_log'
                const path = '/save_progress_log';

                let formData = new FormData();
                formData.append('user_id', this.user_id);
                formData.append('campaign_id', this.campaign_in_progress.campaign_id);
                formData.append('company_id', this.company_id);
                formData.append('question_code', this.question_code);
                formData.append('is_completed', this.is_completed);
                formData.append('progress_rate', progress_rate);

                await this.$axios.post(path, formData);
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 401) {
                        await this.common_401_error_handling();
                    }
                }
            }

        },
        async goForward() {
            let idx = this.another_question_list.findIndex(item => item.question_code == this.question_in_progress.question_code);
            if (idx > -1 && idx < this.another_question_list.length - 1) {
                let new_question_code = this.another_question_list[idx + 1].question_code;
                await this.pageGo(this.another_question_list[idx + 1]);
                this.$emit('move-next-question', new_question_code); //to ProviderAside
            } else {
                alert(this.$t('마지막 문항입니다. 전체 답변을 완료하신 경우 우측의 \'최종 제출하기\' 버튼을 클릭하여 제출해주세요.'));
            }
        },
        async pageGo(question) {
            this.map_save_state_msg = {};
            let temp_question = {...question};
            // console.log(temp_question);
            if (typeof (question.question_contents) === 'string') {
                // string 형태로 가지고 있는 것 parsing 해야함
                temp_question.question_contents = JSON.parse(question.question_contents);
            }
            this.$store.commit('update_question_in_progress', temp_question);

            const {pc, cmp, cpn, cpnm} = this.$route.query;

            await this.$store.dispatch('questions/readSubs', {
                code: temp_question.question_code,
                contents: temp_question.question_contents,
                company_id: cpn,
                campaign_id: cmp,
                path: 'questions'
            });

            if (temp_question.question_code != this.$route.params.question_code) {
                this.$nextTick(() => {
                    this.$router.push({
                        name: 'Questions',
                        query: {pc, cmp, cpn, cpnm},
                        params: {question_code: temp_question.question_code}
                    });
                });
            }
        },
        makeCompleted() {
            if (this.campaign_in_progress.campaign_state !== '답변중' || this.campaign_in_progress.campaign_state !== '생성완료') {
                this.is_completed = 'true';
            }
        },
        handleUpdatePipeline(sub_code, last_save_state) {
            //console.log(this.$refs)
            //  this.$refs.sub_components.forEach(item => {
            //      if(item.item.sub_code != undefined){
            //          item.reInitiate()
            //      }
            //  })
            this.pipeline_key += 1;
            this.$set(this.map_save_state_msg, sub_code, last_save_state);
        },
        copyEmail(){
            navigator.clipboard.writeText('info@qesg.co.kr').then(() => {
                // 성공 시 토스트 메시지 표시 (구현된 EventBus 사용)
                // this.$EventBus.$emit('single-toast-on', { ... });
                this.$EventBus.$emit('toast-success', this.$t('이메일 주소 복사'), this.$t('이메일 주소가 클립보드에 복사되었습니다.'));
            }).catch(err => {
                console.error('클립보드 복사 실패:', err);
            });
        }
    },
};
</script>

<style lang="scss">
.question-blocked {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 50;
    flex-grow: 1;
    height: 100%;
    background: rgb(255, 255, 255, 0.2);
}

.question-block-alert {
    top: 0px;
    height: 60px;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    border: 0;
    border-radius: 0;
    z-index: 60;
}

#question_infobox > a {
    color: var(--blue7);
}

.suggest-submit-alert {
    position: fixed;
    bottom: 0;
    right: 0;
    width: 100%;
}

</style>
